import {IconButton} from '@dropbox/dig-components/buttons';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, SearchLine} from '@dropbox/dig-icons/assets';
import {useState} from 'react';

import {DSYS_RAIL_ROW_INSET_PX, DSYS_TOUCH_TARGET_PX} from './layoutConstants';
import styles from './RailSearchRow.module.css';
import {useHoverShade} from './useHoverShade';

/**
 * A rail's filter, drawn as a rail row: borderless, the magnifier on the rows'
 * text line, the rows' hover fill (and a stronger one while typing). You type in
 * place; Esc or the clear button empties it. With `bleed` (the rail's padding)
 * it runs edge to edge like full-bleed rows; without, it's rounded.
 */
export const RailSearchRow = ({
  value,
  onChange,
  placeholder,
  bleed,
  touch = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  bleed?: {left: number; right: number};
  touch?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  // While typing, the stronger fill replaces the hover's.
  const shade = useHoverShade(hovered && !focused);

  return (
    <Box
      display="flex"
      alignItems="center"
      borderRadius={bleed ? undefined : 'Medium'}
      paddingY="4"
      backgroundColor={focused ? 'Opacity Surface - State 2' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={shade.className}
      style={{
        ...shade.style,
        gap: 8,
        minHeight: touch ? DSYS_TOUCH_TARGET_PX : 36,
        paddingLeft: DSYS_RAIL_ROW_INSET_PX + (bleed?.left ?? 0),
        paddingRight: 8 + (bleed?.right ?? 0),
        marginLeft: bleed ? -bleed.left : undefined,
        marginRight: bleed ? -bleed.right : undefined,
      }}
    >
      <UIIcon src={SearchLine} size="small" />
      <input
        type="text"
        className={styles.input}
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value) {
            event.preventDefault();
            onChange('');
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {value && (
        <IconButton
          variant="transparent"
          size="small"
          aria-label="Clear search"
          onClick={() => onChange('')}
        >
          <UIIcon src={CloseLine} size="small" />
        </IconButton>
      )}
    </Box>
  );
};
